import { useState } from "react"
import {
  Box,
  Typography,
  Button,
  Grid,
  Link,
  Paper,
  Divider,
  ThemeProvider,
  createTheme,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material"
import { KeyboardArrowDown } from "@mui/icons-material"
import {  Link as RouterLink } from "react-router-dom"
import logo from "../../../assets/images/logo.jpeg"
import register from "../../../assets/images/register.png";

// Create a theme with E1X brand colors
const theme = createTheme({
  palette: {
    primary: {
      main: "#E84A1C", // Orange color from the logo
    },
    background: {
      default: "#FFFFFF",
    },
  },
  typography: {
    fontFamily: '"Monserrat", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 4,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          padding: "14px",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
      },
    },
  },
})

export default function ProfessionalExpertise({ data, updateData, onBack, onNext }) {
  const [errors, setErrors] = useState({});

  const handleChange = (event) => {
    const { name, value } = event.target;

    // Convert experience fields to numbers
    const numericFields = ['work_experience', 'skill_experience'];
    const processedValue = numericFields.includes(name)
      ? Number(value)
      : value;

    updateData({ [name]: processedValue });
    setErrors(prev => ({ ...prev, [name]: false }));
  };

  const validateForm = () => {
    const newErrors = {
      role: !data.role,
      work_experience: data.work_experience === undefined || data.work_experience === null,
      skill_experience: data.skill_experience === undefined || data.skill_experience === null,
      primary_skill: !data.primary_skill,      
      english_proficiency: !data.english_proficiency,
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const isFormComplete = () => {
    return (
      data.role &&
      (data.work_experience !== undefined && data.work_experience !== null) &&
      data.primary_skill &&
      (data.skill_experience !== undefined && data.skill_experience !== null) &&
      data.english_proficiency
    );
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onNext();
    }
  };

  const jobRole = ["Software Developer", "Data Scientist", "UX Designer", "Product Manager", "Project Manager", ]

  const primarySkill = ["JavaScript", "Python", "Java", "React", "UI/UX Design"]

  return (
    <ThemeProvider theme={theme}>
      <Grid container component="main" sx={{ height: "100vh" }}>
        {/* Left side - Image */}
        <Grid
          item
          xs={false}
          sm={false}
          md={6}
          sx={{
            position: "relative",
            backgroundSize: "cover",
            backgroundPosition: "center",
            display: { xs: "none", md: "block" },
          }}
        >
          <img
            src={register}
            alt="Student with laptop"
            fill
            style={{ objectFit: "cover" }}
            priority
          />
        </Grid>

        {/* Right side - Professional expertise form */}
        <Grid item xs={12} md={6} component={Paper} elevation={0} square>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "auto",
              px: 4,
              maxWidth: 500,
              mx: "auto",
              py: 4,
            }}
          >
            <Box sx={{ mb: 6, width: "100%", maxWidth: 120 }}>
              <img
                src={logo}
                alt="E1X Academy Logo"
                width={120}
                height={60}
                style={{ objectFit: "contain" }}
              />
            </Box>

            <Typography component="h1" variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
              Share a Brief Overview of Your Professional Background
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Help us understand your experience, strengths, and the value you bring.
            </Typography>

            <Box sx={{ width: "100%", mb: 4 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 1, color: "#000000", fontWeight: "bold", fontFamily: "Monserrat, sans-serif" }}>
                  Role
                </Typography>
                <FormControl fullWidth>
                  <Select
                    name="role"
                    value={data.role}
                    onChange={handleChange}
                    displayEmpty
                    IconComponent={KeyboardArrowDown}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <Typography color="text.secondary">Select your Role</Typography>
                      }
                      return selected
                    }}
                    error={errors.role}
                  >
                    {jobRole.map((role)=>
                      <MenuItem key={role} value={role} sx={{ backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#E84A1C", color: "#ffffff" }}} > {role}</MenuItem>
                    )}

                  </Select>
                  {errors.role && <Typography color="error" variant="caption">Role is required</Typography>}
                </FormControl>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 1, color: "#000000", fontWeight: "bold", fontFamily: "Monserrat, sans-serif" }}>
                  Years Of work Experience
                </Typography>
                <FormControl fullWidth>
                  <Select
                    name="work_experience"
                    value={data.work_experience}
                    onChange={handleChange}
                    displayEmpty
                    IconComponent={KeyboardArrowDown}
                    renderValue={(selected) => {
                      if (selected === undefined || selected === null) {
                        return <Typography color="text.secondary">Select Years</Typography>
                      }
                      const displayText = {
                        0: "Less than 1 year",
                        1: "1-2 years",
                        3: "3-5 years",
                        5: "5+ years",
                        10:"10+ years"
                      };
                      return displayText[selected] || selected;
                    }}
                    error={errors.work_experience}sx={{ backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#E84A1C", color: "#ffffff" }}}
                  >
                    <MenuItem value={0} sx={{ backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#E84A1C", color: "#ffffff" }}} >Less than 1 year</MenuItem>
                    <MenuItem value={1} sx={{ backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#E84A1C", color: "#ffffff" }}} >1-2 years</MenuItem>
                    <MenuItem value={3} sx={{ backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#E84A1C", color: "#ffffff" }}} >3-5 years</MenuItem>
                    <MenuItem value={5} sx={{ backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#E84A1C", color: "#ffffff" }}} >5-10 years</MenuItem>
                    <MenuItem value={10} sx={{ backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#E84A1C", color: "#ffffff" }}} >10+ years</MenuItem>
                  </Select>
                  {errors.work_experience && <Typography color="error" variant="caption">Work experience is required</Typography>}
                </FormControl>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 1, color: "#000000", fontWeight: "bold", fontFamily: "Monserrat, sans-serif" }}>
                  Primary Skill
                </Typography>
                <FormControl fullWidth>
                  <Select
                    name="primary_skill"
                    value={data.primary_skill}
                    onChange={handleChange}
                    displayEmpty
                    IconComponent={KeyboardArrowDown}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <Typography color="text.secondary">Select Skills</Typography>
                      }
                      return selected
                    }}
                    error={errors.primary_skill}
                  >
                    {primarySkill.map((skill)=>

                      <MenuItem key={skill} value={skill} sx={{ backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#E84A1C", color: "#ffffff" } }}>{skill}</MenuItem>
                    )}
                    
                  </Select>
                  {errors.primary_skill && <Typography color="error" variant="caption">Primary skill is required</Typography>}
                </FormControl>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 1, color: "#000000", fontWeight: "bold", fontFamily: "Monserrat, sans-serif" }}>
                  Years of Experience with primary skill
                </Typography>
                <FormControl fullWidth>
                  <Select
                    name="skill_experience"
                    value={data.skill_experience}
                    onChange={handleChange}
                    displayEmpty
                    IconComponent={KeyboardArrowDown}
                    renderValue={(selected) => {
                      if (selected === undefined || selected === null) {
                        return <Typography color="text.secondary">Select Years</Typography>
                      }
                      const displayText = {
                        0: "Less than 1 year",
                        1: "1-2 years",
                        3: "3-5 years",
                        5: "5+ years"
                      };
                      return displayText[selected] || selected;
                    }}
                     
                    error={errors.skill_experience}
                  >
                    <MenuItem value={0} sx={{ backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#E84A1C", color: "#ffffff" }}}>Less than 1 year</MenuItem>
                    <MenuItem value={1} sx={{ backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#E84A1C", color: "#ffffff" }}}>1-2 years</MenuItem>
                    <MenuItem value={3} sx={{ backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#E84A1C", color: "#ffffff" }}}>3-5 years</MenuItem>
                    <MenuItem value={5} sx={{ backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#E84A1C", color: "#ffffff" }}}>5+ years</MenuItem>
                  </Select>
                  {errors.skill_experience && <Typography color="error" variant="caption">Skill experience is required</Typography>}
                </FormControl>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 1, color: "#000000", fontWeight: "bold", fontFamily: "Monserrat, sans-serif" }}>
                  English Proficiency
                </Typography>
                <FormControl fullWidth>
                  <Select
                    name="english_proficiency"
                    value={data.english_proficiency}
                    onChange={handleChange}
                    displayEmpty
                    IconComponent={KeyboardArrowDown}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <Typography color="text.secondary">Select Your English Level</Typography>
                      }
                      return selected
                    }}
                    error={errors.english_proficiency}
                  >
                    <MenuItem value="Basic" sx={{ backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#E84A1C", color: "#ffffff" }}} >Basic</MenuItem>
                    <MenuItem value="Intermediate" sx={{ backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#E84A1C", color: "#ffffff" }}} >Intermediate</MenuItem>
                    <MenuItem value="Advanced" sx={{ backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#E84A1C", color: "#ffffff" }}} >Advanced</MenuItem>
                    <MenuItem value="Fluent" sx={{ backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#E84A1C", color: "#ffffff" }}} >Fluent</MenuItem>
                    <MenuItem value="Native" sx={{ backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#E84A1C", color: "#ffffff" }}} >Native</MenuItem>
                  </Select>
                  {errors.english_proficiency && <Typography color="error" variant="caption">English proficiency is required</Typography>}
                </FormControl>
              </Box>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", mt: "3rem" }}>
              <Button
                variant="outlined"
                sx={{
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  px: 4,
                }}
                onClick={onBack}
              >
                Back
              </Button>

              <Button
                variant="contained"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: "#D15424",
                  },
                  px: 4,
                }}
                disabled={!isFormComplete()}
                onClick={handleSubmit}
              >
                Continue
              </Button>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Already have an account?
              </Typography>
              <Link component={RouterLink} to="talents/login" variant="body2" sx={{ color: theme.palette.primary.main, textDecoration: "none" }}>
                Sign In
              </Link>
            </Box>

            <Divider sx={{ mt: 2 }} />
          </Box>
        </Grid>
      </Grid>
    </ThemeProvider>
  )
}